<template>
  <layout>
    <PageHeader
      class="mc-page-three"
      :routeBack="'/setting/custom-field'"
      :title="title"
      :items="breadcrumbItems"
      hasBreadcrumb
    />
    <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
      <McButtonBack class="d-inline-block me-2" />
      <McButton
        text="Tambah"
        icon="icon-outline_plus"
        class="d-inline-block"
        :disabled="isLoading"
        @click="handleShowCreateModal"
      />
    </div>

    <div class="card">
      <McTable
        ref="table-custom-field-quick-assignment"
        :key="refreshKey"
        :fields="fields"
        :apiClient="apiClient"
        :hasCheckbox="false"
        :hasSelectAll="false"
        :hasDelete="false"
        :searchable="true"
        :isFirstRowClickable="false"
        :initialPage="initialParams.page"
        :initialLimit="initialParams.limit"
        :initialSearch="initialParams.search"
        :initialSorts="initialParams.sorts"
        :withCard="true"
        stickyHeader
      >
        <template #cell(type)="{ value }">
          {{ formatType(value) }}
        </template>
        <template #[`cell(is_active)`]="{ value, item }">
          <McToggle
            :key="toggleKeys[item.id]"
            :value="value"
            @change="handleToggleActive({ item, value: $event })"
          />
        </template>
        <template #[`cell(button_action)`]="{ item }">
          <div
            class="table-custom-field__button-action"
          >
            <b-button
              variant="outline"
              class="table-custom-field__edit-button"
              @click="handleShowFormModal({ item })"
            >
              <i class="mc-icon icon-outline_edit"></i>
            </b-button>

            <b-button
              variant="outline"
              class="table-custom-field__delete-button"
              @click="handleShowDeleteModal({ item })"
            >
              <i class="mc-icon icon-outline_trash"></i>
            </b-button>
          </div>
        </template>
      </McTable>
    </div>

    <ModalForm
      ref="modal-form"
      :formType="formType"
      :selectedItem="selectedItem"
      @reloadList="handleReloadList"
    />
    <ModalDelete
      ref="modal-delete"
      :selectedItem="selectedItem"
      @submit="handleDelete"
    />
    <ModalToggleActive
      ref="modal-toggle-active"
      :isLoading="isLoading"
      :isToggleActive="isToggleActive"
      :selectedItem="selectedItem"
      @submit="handleConfirmToggle"
      @update:toggleKey="handleUpdateToggleKey"
    />
  </layout>
</template>

<script>
import appConfig from '@/app.config'
import { computed } from 'vue'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McTable from '@/components/McTable'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McToggle from '@/components/McToggle'

import ModalForm from './components/ModalForm'
import ModalDelete from './components/ModalDelete'
import ModalToggleActive from './components/ModalToggleActive'

import fields from './lib/fields/quick-assignment-index-fields'
import { typeItems } from './lib/constants/type-items'

import * as CustomField from '@/services/rest-api/lib/setting/quick-assignment/custom-field'

import isEqual from 'lodash/isEqual'

export default {
  page: {
    title: 'Setting',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    McTable,
    McButton,
    McButtonBack,
    McToggle,
    ModalForm,
    ModalDelete,
    ModalToggleActive,
  },
  mixins: [fields],
  provide() {
    return {
      selectedItem: computed(() => this.selectedItem),
      typeItems: computed(() => this.typeItems),
    }
  },
  data() {
    return {
      typeItems,
      params: {},
      initialParams: {
        page: 1,
        limit: 10,
        search: '',
        sorts: [
          {
            key: 'created_on',
            order: 'asc',
          }
        ],
      },
      toggleKeys: {},
      selectedItem: {},

      formType: null,
      refreshKey: 0,
      isLoading: false,
      isToggleActive: false,
    }
  },
  computed: {
    title() {
      return 'Quick Assignment'
    },
    breadcrumbItems() {
      return [
        {
          text: this.$t('setting.list.development.setting'),
          to: '/setting',
        },
        {
          text: 'Kolom Custom',
          to: '/setting/custom-field',
        },
        {
          text: 'Quick Assignment',
        }
      ]
    },
    apiClient() {
      return {
        get: async (args) => {
          this.isLoading = true

          args = {
            ...args,
            filters: ['entity eq FLEET_TASK_INSTANT'],
          }

          const res = await CustomField.get(args, this.handleSetParams)

          res.data.paginated_result.forEach((item) => {
            this.$set(this.toggleKeys, item.id, 1)
          })

          this.isLoading = false

          return res
        }
      }
    }
  },
  created() {
    this.setFilterFromURL()
  },
  methods: {
    formatType(value) {
      const type = typeItems.find((el) => el.key == value).title
      return this.$t(type)
    },

    handleSetParams(params) {
      this.params = params
      this.setURLParams(params)
    },

    setURLParams(params) {
      const newParams = {}

      // loop the params to set the newParams
      for (const [key, val] of Object.entries(params)) {
        // skip the undefined value
        if (val == undefined) continue

        // immediately set the value if it's not an Array
        if (!Array.isArray(val)) {
          newParams[key] = typeof val === 'string' ? val : val.toString()

          continue
        }
      }

      // check if the URL has same values with new params
      const isSameParams = isEqual(newParams, this.$route.query)
      if (isSameParams) return

      // redirect to new query params
      const urlPath = '/setting/custom-field/quick-assignment?' + new URLSearchParams(newParams).toString()
      this.$router.replace(urlPath)
    },
    
    // change filters from current URL
    setFilterFromURL() {
      for (const [key, val] of Object.entries(this.$route.query)) {
        // set initial params exclude filters (page, limit, search)
        if (!key.includes('filters')) {
          let value
          if (key === 'search') {
            value = val || '-'
          } else if (key === 'count') {
            value = ['10', '25', '50', '100'].includes(val) ? Number(val) || 1 : 10
          } else if (key === 'page') {
            value = Number(val) || 1
          }

          this.$set(this.initialParams, key, value)
          continue
        }
      }
    },
    handleShowCreateModal() {
      this.formType = 'create'
      this.$refs['modal-form'].showModal()
    },
    handleToggleActive({ item, value }) {
      this.isToggleActive = value
      this.selectedItem = item

      this.$refs['modal-toggle-active'].showModal()
    },
    async handleConfirmToggle() {
      this.isLoading = true
      const res = await CustomField.update(this.selectedItem.id, { is_active: this.isToggleActive })

      if (res.errors) {
        return
      }

      this.selectedItem = {}
      this.$refs['modal-toggle-active'].hideModal()
      this.$mcToast.success(`Berhasil ${this.isToggleActive ? 'diaktifkan' : 'dinonaktifkan'}.`)

      this.refreshKey += 1
    },
    handleUpdateToggleKey() {
      const id = this.selectedItem.id
      const currentKey = this.toggleKeys[id]

      this.$set(this.toggleKeys, id, currentKey + 1)
    },
    async handleShowFormModal({ item }) {
      this.formType = 'update'
      const res = await CustomField.show(item.id)

      if (res.errors) return

      this.selectedItem = res.data
      this.$refs['modal-form'].showModal('form')
    },
    handleShowDeleteModal({ item }) {
      this.selectedItem = item
      this.$refs['modal-delete'].showModal()
    },
    async handleDelete() {
      this.isLoading = true

      const res = await CustomField.deleteSingle(this.selectedItem.id)

      if (res.errors) return

      this.selectedItem = {}
      this.$mcToast.success('Berhasil dihapus.')
      this.$refs['modal-delete'].hideModal()
      this.refreshKey += 1
      
      this.isLoading = false
    },
    handleReloadList() {
      this.refreshKey += 1
    }
  }
}
</script>

<style lang="scss" scoped>
.mc-has-search {
  border-width: $input-border-width;
  border-color: $input-border-color;
  border-style: solid;
  border-radius: 4px;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    border-radius: 0;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon:before {
      font-size: 16px;
      color: $input-placeholder-color;
    }
  }
}

.table-custom-field {
  &__edit-button,
  &__delete-button {
    i {
      color: $mep-color-text-400;
    }
  }

  &__delete-button {
    i:before {
      color: $red;
    }
  }
}
</style>